<template>
  <div class="rating-and-reviews margin-bottom-m">
    <h2 class="h3 margin-bottom-m title">Ratings & Reviews</h2>
    <GeneratedSummary v-bind="hydrateWhenProps" />
    <div class="grid-x">
      <div class="cell large-2 small-12">
        <AverageRating :reviews-count="totalResults" :total-count="totalCount" :average-rating="averageRating" />
      </div>

      <div class="large-1 cell"></div>

      <div class="stars-rating-snapshot cell large-4 medium-5 small-12">
        <StarRatingSnapshot :count-per-rating="countPerRating" />
      </div>

      <div class="large-1 medium-1 hide-for-small-only cell"></div>

      <div v-if="sliders && sliders.length" class="cell large-4 medium-6 small-12 grid-x align-right small-margin-top-m medium-margin-top-z">
        <div class="fitsliders grid-y cell">
          <FitSlider
            v-for="({ value, valueRange, title, minLabel, midLabel, maxLabel }, index) in sliders"
            v-bind="hydrateWhenProps"
            :key="title + index"
            :value="value"
            :value-range="valueRange"
            :title="title"
            :labels="{ minLabel, midLabel, maxLabel }"
          />
        </div>
      </div>

      <div
        v-if="photos.length"
        :data-testid="reviewsStatisticsPhotosBlock"
        :class="`cell ${photoBlockMargins} ${photosDesktopWidth} small-12 customer-images-container`"
      >
        <CustomerImagesBlock :is-on-two-lines="isPhotosOnSecondLine" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { defineLazyVisibleHydratedComponent, defineLazyIfHydratedComponent } from '@nuxt-common/media-card-large/app/modules/lazy-hydration';
import useKillswitches from '~/modules/featureControl/composables/useKillswitches';

import useReviewsPhotos from '~/modules/reviews/composables/useReviewsPhotos';
import useReviewsStatistics from '~/modules/reviews/composables/useReviewsStatistics';

import StarRatingSnapshot from './StarRatingSnapshot';
import StaticImportFitSlider from '../FitSlider';
import StaticImportCustomerImagesBlock from './CustomerImagesBlock';
import AverageRating from './AverageRating';
import StaticImportGeneratedSummary from '../GeneratedSummary';
import useFeatureEligibility from '~/modules/featureControl/composables/useFeatureEligibility';
import { useProductFlags } from '~/modules/featureControl/composables/useProductFlags';
import { reviewsStatisticsPhotosBlock } from '../../constants/testIds';

const { isConsolidatedThirdVariantFlag } = useProductFlags();
const { isShopTheSetExpEnabled } = useFeatureEligibility();

const { killswitches } = useKillswitches();
let hydrateWhenProps = {};
let GeneratedSummary = StaticImportGeneratedSummary,
  FitSlider = StaticImportFitSlider,
  CustomerImagesBlock = StaticImportCustomerImagesBlock;

// TODO: remove later once lazy hydration testing is over
if (killswitches.isLazyHydrationEnabled) {
  hydrateWhenProps = { hydrateWhen: isConsolidatedThirdVariantFlag || isShopTheSetExpEnabled };

  GeneratedSummary = defineLazyIfHydratedComponent(() => import('../GeneratedSummary.vue'));
  FitSlider = defineLazyIfHydratedComponent(() => import('../FitSlider.vue'));
  CustomerImagesBlock = defineLazyVisibleHydratedComponent(() => import('./CustomerImagesBlock.vue'));
}

defineProps({
  totalResults: {
    type: Number,
    default: 0,
  },
});

const { photos } = useReviewsPhotos();
const { averageRating, totalCount, countPerRating, sliders } = useReviewsStatistics();

const isPhotosOnSecondLine = computed(() => (sliders.value.length ? true : photos.value.length > 3));
const photosDesktopWidth = computed(() => `large-${isPhotosOnSecondLine.value ? 12 : 4}`);
const photoBlockMargins = computed(() =>
  isPhotosOnSecondLine.value ? 'large-margin-top-xl small-margin-top-m' : 'large-margin-top-z small-margin-top-m',
);
</script>

<style scoped lang="scss">
@import '~settingSCSS';
@import '~@core/csg/src/scss/typography/typography-mixins.scss';

.stars-rating-snapshot {
  display: flex;
  flex-direction: column;
  gap: rem-calc(16);
}
.fitsliders {
  gap: rem-calc(24);
}
.customer-images-container {
  z-index: 1;
}
// this was changed from h3 tag to h2
// override is added to comply with previous spacing, that is overriden on Product.vue level
// when decided to remmove override for h3 spacing on Product.vue - this can be deleted too
// There is TODO for that TD#1
.title {
  @include breakpoint(large up) {
    margin-bottom: rem-calc(24) !important;
  }
}

@include breakpoint(small only) {
  .title {
    @include header(h4);
  }
}
</style>
